import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL, PUT_URL } from '@utils/api/endpoints.ts';
import { DocumentMutationIdentifier } from '@utils/types/editors/transcripts/api.ts';
import { DocumentResult } from '@utils/types/editors/generic.ts';
import { QueryKeys } from '@utils/api/query-keys.ts';

type ExtendedDocumentMutationIdentifier = DocumentMutationIdentifier & {
  folderId?: string;
  dontInvalidateQueries?: boolean;
};

export function useResetDocument() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      sessionId,
      documentId
    }: ExtendedDocumentMutationIdentifier): Promise<DocumentResult> => {
      // Reset the document
      await Axios.put(PUT_URL(sessionId, documentId).resetDocument);

      // Fetch the fresh document data
      const documentUrl = GET_URL(sessionId, documentId).document;
      const { data } = await Axios.get<DocumentResult>(documentUrl);

      // Update the query cache with fresh data
      queryClient.setQueryData(QueryKeys.document(sessionId, documentId), data);

      return data;
    },
    {
      onSuccess: async (
        _freshDocument,
        request: ExtendedDocumentMutationIdentifier
      ) => {
        if (request?.dontInvalidateQueries) return;

        // Invalidate related queries to keep UI in sync
        if (request.folderId) {
          await queryClient.invalidateQueries(
            QueryKeys.folders(request.folderId)
          );
        } else {
          await queryClient.invalidateQueries(QueryKeys.folders());
        }

        await queryClient.invalidateQueries(
          QueryKeys.session(request.sessionId)
        );
      }
    }
  );
}
